/**
 * Date formatting and manipulation utilities
 */

const DEFAULT_LOCALE = 'fr-CA'

/**
 * Format a date for display with various options
 * @param {Date|string} date - The date to format
 * @param {string} format - The format style: 'short', 'medium', 'long', or 'full'
 * @param {string} locale - The locale to use for formatting
 * @returns {string} The formatted date string
 */
export function formatDate(date, format = 'short', locale = DEFAULT_LOCALE) {
  if (!date)
    return 'Non programmé'
  const dateObj = date instanceof Date ? date : new Date(date)
  return dateObj.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Formats a date with the day of the week, day, and month
 * @param {Date|string} date - The date to format
 * @param {string} locale - The locale to use for formatting
 * @returns {string} The formatted date string with weekday, day, and month
 */
export function formatDayDate(date, locale = DEFAULT_LOCALE) {
  if (!date)
    return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  return dateObj.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

/**
 * Format month and year
 * @param {Date|string} date - The date to format
 * @param {string} locale - The locale to use for formatting
 * @returns {string} The formatted month and year string (e.g., "Juin 2024")
 */
export function formatMonthYear(date, locale = DEFAULT_LOCALE) {
  if (!date)
    return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  return dateObj.toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  })
}

/**
 * Format date with short month representation
 * @param {Date|string} date - The date to format
 * @param {string} locale - The locale to use for formatting
 * @returns {string} The formatted date with short month (e.g., "15 juin")
 */
export function formatShortDate(date, locale = DEFAULT_LOCALE) {
  if (!date)
    return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  return dateObj.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
  })
}

/**
 * Format date in custom format with day, short month and year
 * @param {Date|string} date - The date to format
 * @param {string} locale - The locale to use for formatting
 * @returns {string} The formatted date (e.g., "15 juin 2024")
 */
export function formatDateCustom(date, locale = DEFAULT_LOCALE) {
  if (!date)
    return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  const day = dateObj.getDate()
  const month = dateObj.toLocaleString(locale, { month: 'short' })
  const year = dateObj.getFullYear()
  return `${day} ${month} ${year}`
}

/**
 * Format date and time with weekday
 * @param {Date|string} date - The date to format
 * @param {string} locale - The locale to use for formatting
 * @returns {string} The formatted date and time string
 */
export function formatDateTime(date, locale = DEFAULT_LOCALE) {
  if (!date)
    return ''

  const dateObj = date instanceof Date ? date : new Date(date)
  return dateObj.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format time only
 * @param {Date|string} date - The date to extract time from
 * @param {string} locale - The locale to use for formatting
 * @returns {string} The formatted time string
 */
export function formatTime(date, locale = DEFAULT_LOCALE) {
  if (!date)
    return ''

  const dateObj = date instanceof Date ? date : new Date(date)
  return dateObj.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Convert a date to ISO string format
 * @param {Date|string} date - The date to format
 * @returns {string} The ISO string representation of the date
 */
export function formatISOString(date) {
  // Ensure date is a Date object
  const dt = date instanceof Date ? date : new Date(date)
  return dt.toISOString()
}

/**
 * Check if a given date is today
 * @param {Date|string} date - The date to check
 * @returns {boolean} True if the date is today, false otherwise
 */
export function isToday(date) {
  if (!date)
    return false

  const inputDate = date instanceof Date ? date : new Date(date)
  const today = new Date()

  return (
    inputDate.getDate() === today.getDate()
    && inputDate.getMonth() === today.getMonth()
    && inputDate.getFullYear() === today.getFullYear()
  )
}

/**
 * Check if a date is in the past
 * @param {Date|string} date - The date to check
 * @returns {boolean} True if the date is in the past, false otherwise
 */
export function isPast(date) {
  if (!date)
    return false
  const inputDate = date instanceof Date ? date : new Date(date)
  return inputDate < new Date()
}

/**
 * Format file size in bytes to human-readable format
 * @param {number} bytes - Size in bytes
 * @returns {string} Human-readable size (e.g., "2.5 MB")
 */
export function formatFileSize(bytes) {
  if (!bytes || isNaN(bytes))
    return 'Unknown'

  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`
}

/**
 * Calculate time difference between now and a target date
 * @param {Date|string} targetDate - The target date to compare with
 * @param {string} locale - The locale to use for formatting
 * @returns {string} Formatted time difference
 */
export function timeUntil(targetDate, locale = DEFAULT_LOCALE) {
  if (!targetDate)
    return ''

  const now = new Date()
  const meetingStart = new Date(targetDate)

  if (now >= meetingStart)
    return ''

  const diffMs = meetingStart - now
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor(
    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  )
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

  const pluralize = (value, singular, plural) =>
    `${value} ${value > 1 ? plural : singular}`

  if (diffDays > 0) {
    return `Dans ${pluralize(diffDays, 'jour', 'jours')} et ${pluralize(diffHours, 'heure', 'heures')}`
  }

  if (diffHours > 0) {
    return `Dans ${pluralize(diffHours, 'heure', 'heures')} et ${pluralize(diffMinutes, 'minute', 'minutes')}`
  }

  return `Dans ${pluralize(diffMinutes, 'minute', 'minutes')}`
}

/**
 * Format date and time with full style (weekday, day, month, year, hour, minute)
 * @param {Date|string} date - The date to format
 * @param {string} locale - The locale to use for formatting
 * @returns {string} The formatted date and time string with full details
 */
export function formatDateTimeFull(date, locale = DEFAULT_LOCALE) {
  if (!date)
    return ''

  const dateObj = date instanceof Date ? date : new Date(date)
  return dateObj.toLocaleString(locale, {
    dateStyle: 'full',
    timeStyle: 'short',
  })
}
